<template lang="pug">
  y-dialog(v-model="showDialog" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="600" persistent scrollable content-class="round-3xl")
    v-card
      v-card-title.pa-4
        v-row(no-gutters align="center")
          v-col(cols="10" offset="1")
            div.text-xl.font-bold.neutral700--text.text-center(v-if="register") Mark as arrived and register patient
            div.text-xl.font-bold.neutral700--text.text-center(v-else) {{ $t('mark_as_arrived') }}
          v-col(cols="1" align="right")
            v-btn.ml-n3(text icon @click="closeDialog")
              v-icon(color="neutral700" :size="24") mdi-close
      v-divider
      v-card-text(:class="$vuetify.breakpoint.xsOnly ? 'pa-4' : 'pa-8'")
        div.d-flex.justify-center.mt-16(v-if="isFetching")
          v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

        template(v-else-if="appointment")
          div.text-md.font-semibold.neutral700--text(v-if="register") This patient will be marked as arrived and registered for consultation. Are you sure you want to proceed with this action?
          div.text-md.font-semibold.neutral700--text(v-else) {{ $t('mark_patient_arrived_confirmation') }}
          div.mt-6
            template(v-for="(info, idx) in infoList")
              div.d-flex.align-center.px-6.py-4(:key="'info-' + idx")
                div
                  div.text-xs.neutral500--text {{ info.text }}
                  div.text-sm.neutral700--text {{ info.value }}
                v-spacer
                v-btn(icon small @click="copyText(info.value)")
                  v-icon $yzi-copy-03
              v-divider
          div(ref="copy")

      v-divider
      v-card-actions.py-4(:class="$vuetify.breakpoint.xsOnly ? 'px-4' : 'px-8'")
        template(v-if="register")
          //- v-btn.button-md.px-0(:loading="isLoading" color="neutral700" block outlined depressed dark @click="confirmButHasChanges") Yes, but change patient details
          v-btn.button-md.neutral700--text.px-0(@click="closeDialog" text) {{ $t('cancel') }}
          v-spacer
          v-btn.button-md(:loading="isLoading" color="neutral700" depressed dark @click="confirmButHasChanges") {{ $t('confirm') }}
        template(v-else)
          v-btn.button-md.neutral700--text.px-0(@click="closeDialog" text) {{ $t('cancel') }}
          v-spacer
          v-btn.button-md(:loading="isLoading" color="neutral700" depressed dark @click="confirmAction") {{ $t('confirm') }}

</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'MarkAsArrived',
  components: {},
  props: ['open', 'selectedAppointment', 'register'],
  data () {
    return {
      isFetching: false,
      isLoading: false,
      appointment: null,
      infoList: [],
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (val) {
        this.$emit('update:open', val)
      }
    }
  },
  mounted () {
    this.fetchAppointment()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchAppointment () {
      this.isFetching = true
      this.$axios.get(`/clinics/${this.company.slug}/appointments/${this.selectedAppointment.id}`)
        .then(resp => {
          this.appointment = resp.data
          this.infoList = [
            { text: this.$t('name'), value: this.appointment.lead.name || '-' },
            { text: this.$t('nric_passport'), value: this.appointment.lead.ic || '-' },
            { text: this.$t('phone_number'), value: this.appointment.lead.phone ? `+${this.appointment.lead.phone}` : '-' },
            { text: this.$t('email'), value: this.appointment.lead.email || '-' },
            { text: this.$t('address'), value: this.checkEmptyAddress(this.appointment.patient.address) ? '-' : this.getAddressInString(this.appointment.patient.address) },
          ]
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    confirmButHasChanges () {
      this.isLoading = true
      this.registerPatient(false)
    },
    confirmAction (autoRegister) {
      this.isLoading = true
      const endpoint = `/clinics/${this.company.slug}/appointments/${this.selectedAppointment.id}`
      const data = { status: 'received' }
      this.$axios.patch(endpoint, data)
        .then(resp => {
          this.updateActivityLog(endpoint, data, resp, 'PATCH', this.selectedAppointment.id)
          if (this.register) {
            this.registerPatient(autoRegister)
          } else {
            this.closeResetDialog()
          }
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          if (!this.register) {
            this.isLoading = false
          }
        })
    },
    registerPatient (autoRegister) {
      let data = {
        provider: this.appointment.provider.id,
        remarks: this.appointment.service.name,
        patient: this.appointment.patient.id,
        code: 2,
        appointment: this.appointment.id,
        default_payment: { type: 'self-pay', note: ''}
      }
      if (autoRegister) {
        this.$axios.post(`/clinics/${this.company.slug}/registrations`, data)
        .then(() => {
          this.showMessage(['success', 'Patient registered successfully'])
          this.closeResetDialog()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
      } else {
        this.$emit('prefill', data)
      }
    },
    closeResetDialog () {
      this.showDialog = false
      this.$emit('reset')
    },
    closeDialog () {
      this.showDialog = false
      this.$emit('closed')
    },
    checkEmptyAddress (address) {
      if (!address) {
        return true
      }
      if (Object.keys(address).length === 0) {
        return true
      } else if (Object.values(address).every(val => !val)) {
        return true
      }
      return false
    },
    getAddressInString (addr) {
      let address = ''
      if (addr.address_1) {
        address += addr.address_1
      } if (addr.address_2) {
        address += `, ${addr.address_2}`
      } if (addr.postcode) {
        address += `, ${addr.postcode}`
      } if (addr.city) {
        address += `, ${addr.city}`
      } if (addr.state) {
        address += `, ${addr.state}`
      } if (addr.country) {
        address += `, ${addr.country}`
      }
      return address
    },
    copyText (text) {
      let container = this.$refs.copy
      this.$copyText(text, container)
        .then(() => {
          this.showMessage(['success', this.$t('success_message.content_copied_to_clipboard')])
        })
    },
    updateActivityLog (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'appointment',
        activity: 'Update appointment as arrived',
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
</style>